// Copy para el vendedor por code de error de retail-promotions-business.
import { ApiError } from '@/api/errors';
import { RequestTimeoutError } from '@/api/client';
import { PromotionsServiceNotConfiguredError } from '@/domains/retail/admin/promotions/api/promotions.client';
import { PAYMENT_NOT_ELIGIBLE_MESSAGE } from '@/domains/sales/wizard/chargeErrors';
import type { RetailPromotionsErrorCode } from '../api/wizardPromotionsContract';
import type { PromotionPaymentOption } from '@/domains/retail/admin/promotions/api/promotionsContract';
import { paymentOptionsShortText } from '@/domains/retail/admin/promotions/lib/salePromotionFormOptions';
import type { DiscountCodeBlockCode } from './discountCodeBlock';
import type { DroppedCode } from './promotionSelection';

const NO_PERMISSION = 'No tienes permiso para aplicar promociones en esta tienda.';
const GENERIC = 'No pudimos aplicar la promo. Intenta de nuevo.';

const UPSTREAM_DOWN = 'Un servicio no respondió. Vuelve a intentarlo.';

const PAYMENT_NOT_ELIGIBLE = 'PROMOTION_PAYMENT_NOT_ELIGIBLE';
const PAYMENT_EXITS = 'Cambia el medio de cobro o quita la promo.';

// El servidor lo manda con folio o con cobro: no se afirma que ya se cobró.
const QUOTED_COPY =
    'La venta ya tiene folio o cobro; ya no puedes cambiar la promo, el regalo ni el código.';

const DISCOUNT_CODE_BLOCKED_COPY: Record<DiscountCodeBlockCode, string> = {
    DISCOUNT_AFTER_QUOTE: 'La venta ya tiene folio o cobro; el código ya no se puede cambiar.',
};

type CopiedCode = RetailPromotionsErrorCode | 'PERMISSION_DENIED';

const COPY_BY_CODE: Partial<Record<CopiedCode, string>> = {
    PROMOTION_NOT_ACTIVE: 'Esta promo ya no está vigente. Elige otra.',
    PROMOTION_NOT_APPLICABLE:
        'La venta o el cliente no cumplen esta promo, o el cliente ya la usó.',
    CUSTOMER_NOT_IDENTIFIED: 'Identifica al cliente para usar esta promo.',
    GIFT_NOT_ALLOWED: 'Ese regalo no está incluido en la promo. Elige otro.',
    INSUFFICIENT_STOCK: 'El regalo se quedó sin existencia. Elige otro.',
    CODE_NOT_FOUND: 'No encontramos ese código. Revisa que esté bien escrito.',
    CODE_NOT_APPLICABLE: 'Este código no aplica a este equipo o a esta tienda.',
    CODE_NOT_OWNED: 'Este cupón es de otro cliente.',
    CODE_NOT_ACTIVE: 'Este código venció o fue anulado.',
    CODE_EXHAUSTED: 'Este código ya no tiene usos disponibles.',
    CODE_ALREADY_APPLIED: 'La venta ya tiene un código. Quítalo para usar otro.',
    DISCOUNT_AFTER_QUOTE: QUOTED_COPY,
    PROMOTION_NOT_REVALIDATED: 'Cambió la venta. Vuelve a aplicar la promo.',
    SALE_PROMOTION_HOLD_CONFLICT: 'Alguien más cambió la venta; vuelve a intentarlo.',
    PROMOTION_KIND_MISMATCH: 'Esta promo no se puede elegir aquí.',
    WIZARD_SESSION_NOT_FOUND: 'No encontramos la venta en esta tienda.',
    UPSTREAM_UNAVAILABLE: UPSTREAM_DOWN,
    MISSING_STORE_SCOPE: NO_PERMISSION,
    MISSING_TENANT_SCOPE: NO_PERMISSION,
    PERMISSION_DENIED: NO_PERMISSION,
    PROMOTION_PAYMENT_NOT_ELIGIBLE: PAYMENT_NOT_ELIGIBLE_MESSAGE,
};

export function errorCodeOf(error: unknown): string | undefined {
    return error instanceof ApiError ? error.code : undefined;
}

/** Por qué una promo no se suma, con la que choca si se conoce su nombre. */
function notStackableCopy(error: ApiError, nameOf?: (promotionId: string) => string | undefined) {
    const data = (error.data ?? {}) as { reason?: string; promotionId?: string; details?: unknown };
    const details = (data.details ?? data) as { reason?: string; promotionId?: string };
    switch (details.reason) {
        case 'ONE_COUPON_PER_SALE':
            return 'Solo un cupón por venta.';
        case 'ONE_WARRANTY_PER_SALE':
            return 'Solo una garantía por venta.';
        case 'DUPLICATE_PROMOTION':
            return 'Esa promo ya está en la venta.';
        case 'ONE_PER_KIND':
            return 'Solo una de ese tipo por venta.';
        default: {
            const other = details.promotionId ? nameOf?.(details.promotionId) : undefined;
            return other
                ? `Esa promo no se junta con ${other} en este producto.`
                : 'Esa promo no se junta con otra que va sola en este producto.';
        }
    }
}

/** Mensaje para el vendedor; nunca inventa una causa que el code no dice. */
export function promotionErrorCopy(
    error: unknown,
    nameOf?: (promotionId: string) => string | undefined,
): string {
    if (error instanceof PromotionsServiceNotConfiguredError) {
        return 'Las promociones no están disponibles en este ambiente.';
    }
    if (error instanceof RequestTimeoutError) return error.message;
    if (error instanceof ApiError && error.code === 'PROMOTION_NOT_STACKABLE') {
        return notStackableCopy(error, nameOf);
    }
    const code = errorCodeOf(error);
    if (code && Object.hasOwn(COPY_BY_CODE, code)) {
        return rejectionCopy(code as CopiedCode) ?? GENERIC;
    }
    if (error instanceof ApiError && error.httpStatus === 502) return UPSTREAM_DOWN;
    return GENERIC;
}

/** Errores que se avisan con toast: no se suma, la venta cambió por fuera, ya se cobró o falló un servicio. */
export function isToastPromotionError(error: unknown): boolean {
    const code = errorCodeOf(error);
    return (
        code === 'PROMOTION_NOT_STACKABLE' ||
        code === 'SALE_PROMOTION_HOLD_CONFLICT' ||
        code === 'DISCOUNT_AFTER_QUOTE' ||
        (error instanceof ApiError && error.httpStatus === 502)
    );
}

/** El error dejó la sesión distinta a la que se ve: hay que releerla. */
export function needsSessionRefresh(error: unknown): boolean {
    const code = errorCodeOf(error);
    return code === 'SALE_PROMOTION_HOLD_CONFLICT' || code === 'DISCOUNT_AFTER_QUOTE';
}

const CHARGED_LOCK_COPY = {
    gift: 'Ya se cobró el enganche; el regalo ya no se puede cambiar.',
    sale: 'Ya se cobró el enganche; la promo de venta ya no se puede cambiar.',
} as const;

/** Por qué la pieza ya no se puede cambiar tras el cobro. */
export function chargedLockCopy(piece: keyof typeof CHARGED_LOCK_COPY): string {
    return CHARGED_LOCK_COPY[piece];
}

/** Por qué ya no se puede poner código, según el code que lo bloqueó. */
export function discountCodeBlockedCopy(code: DiscountCodeBlockCode): string {
    return DISCOUNT_CODE_BLOCKED_COPY[code];
}

/** Motivo de un rechazo de Promociones, si tiene copy propio. */
export function rejectionCopy(code: CopiedCode | null): string | undefined {
    if (!code) return undefined;
    return COPY_BY_CODE[code];
}

/** Aviso de un código que se quitó al reaplicar la venta. */
export function droppedCodeCopy({ code, rejectionCode }: DroppedCode): string {
    const reason = rejectionCopy(rejectionCode);
    return reason
        ? `Quitamos el código ${code}. ${reason}`
        : `Quitamos el código ${code}: ya no aplica a esta venta.`;
}

const BLOCKED_COPY: Partial<Record<RetailPromotionsErrorCode, string>> = {
    CUSTOMER_NOT_IDENTIFIED: 'Identifica al cliente para confirmarla',
    INSUFFICIENT_STOCK: 'El regalo no tiene existencia',
};

/** Motivo corto de una promo que se ve pero no se puede elegir (`blockedBy`). */
export function blockedReasonCopy(code: RetailPromotionsErrorCode): string {
    return BLOCKED_COPY[code] ?? 'No disponible para esta venta';
}

/** Por qué se soltó una promo: solo aplica con SureKeep o, sin saber más (pausa o cliente), ya no aplica. */
export type DroppedPromotionReason = 'sureKeepOnly' | 'unavailable';

/** Aviso de una promo que se soltó de la venta. */
export function droppedPromotionCopy(
    name?: string,
    reason: DroppedPromotionReason = 'unavailable',
): string {
    const promo = name ?? 'una promo';
    return reason === 'sureKeepOnly'
        ? `Quitamos ${promo} de la venta: solo aplica con crédito SureKeep.`
        : `Quitamos ${promo} de la venta: ya no aplica a esta venta.`;
}

/** No se pudo soltar lo solo SureKeep al pasar a contado; la venta no avanza hasta lograrlo. */
export function sureKeepReleaseFailedCopy(names: readonly string[], error?: unknown): string {
    const what = names.length > 0 ? names.join(', ') : 'las promos solo SureKeep';
    if (errorCodeOf(error) === 'DISCOUNT_AFTER_QUOTE') {
        return `No pudimos quitar ${what}: ${lowerFirst(QUOTED_COPY)}`;
    }
    return `No pudimos quitar ${what} de la venta. Reintenta.`;
}

/** Una promo o código soltado de contado que ya no aplica al volver a SureKeep. */
export function restoreDroppedCopy(what: string, reason?: string): string {
    return reason
        ? `No regresó ${what}. ${reason}`
        : `No regresó ${what}: ya no aplica a esta venta.`;
}

function lowerFirst(text: string): string {
    return text.charAt(0).toLocaleLowerCase('es-MX') + text.slice(1);
}

/** «La promo «X» solo aplica pagando con Y.» y las dos salidas; `undefined` si el error es otro. */
export function paymentNotEligibleCopy(error: unknown): string | undefined {
    if (errorCodeOf(error) !== PAYMENT_NOT_ELIGIBLE) return undefined;
    const data = ((error as ApiError).data ?? {}) as {
        promotionName?: string;
        acceptedOptions?: string;
    };
    const accepted = (data.acceptedOptions ?? '')
        .split(',')
        .filter(Boolean) as PromotionPaymentOption[];
    if (!data.promotionName || accepted.length === 0) return PAYMENT_NOT_ELIGIBLE_MESSAGE;
    return `La promo «${data.promotionName}» solo aplica pagando con ${paymentOptionsShortText(accepted)}. ${PAYMENT_EXITS}`;
}

/** «Quitamos la promo «X»: no aplica pagando con crédito SureKeep.» al cambiar la forma de pago. */
export function paymentDroppedNoticeCopy(name: string | undefined, sureKeep: boolean): string {
    const promo = name ? `la promo «${name}»` : 'una promo';
    return `Quitamos ${promo}: no aplica pagando ${sureKeep ? 'con crédito SureKeep' : 'de contado'}.`;
}

/** No se pudo revisar la venta con la nueva forma de pago; el paso no avanza hasta lograrlo. */
export function paymentChangeFailedCopy(names: readonly string[], error?: unknown): string {
    const what = names.length > 0 ? names.join(', ') : 'las promos de la venta';
    if (errorCodeOf(error) === 'DISCOUNT_AFTER_QUOTE') {
        return `No pudimos revisar ${what}: ${lowerFirst(QUOTED_COPY)}`;
    }
    return `No pudimos revisar ${what} con la nueva forma de pago. Reintenta.`;
}
